import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Dumbbell, PlayCircle, TrendingUp, MessageCircle, Calendar, Smartphone, ArrowRight, Check, Apple } from 'lucide-react';
import { Logo } from '../../Logo';
const TRAINERIZE_URL = 'https://www.trainerize.me/profile/structured/?planGUID=0adc7cc9d3274ca183f4a7ca2a0e16a3';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
// Phone mockup component — stylized "Structured" workout UI
function PhoneMockup() {
  return <div className="relative mx-auto w-[280px] md:w-[320px]" data-id="element-1996">
      {/* Phone frame */}
      <div className="relative bg-charcoal rounded-[3rem] p-3 shadow-2xl border-[3px] border-darkgray" data-id="element-1997">
        {/* Notch */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-charcoal rounded-b-2xl z-20" data-id="element-1998" />

        {/* Screen */}
        <div className="bg-bodybg rounded-[2.3rem] overflow-hidden aspect-[9/19.5] relative" data-id="element-1999">
          {/* Status bar */}
          <div className="flex justify-between items-center px-6 pt-4 pb-2 text-[10px] text-white" data-id="element-2000">
            <span className="font-bold" data-id="element-2001">9:41</span>
            <span className="opacity-70" data-id="element-2002">●●●●●</span>
          </div>

          {/* App content */}
          <div className="px-5 py-4" data-id="element-2003">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 pt-4" data-id="element-2004">
              <Logo className="w-7 h-7" data-id="element-2005" />
              <span className="text-[8px] font-bold text-[#9A9A90] uppercase tracking-[0.25em]" data-id="element-2006">
                Structured
              </span>
            </div>

            {/* Greeting */}
            <p className="text-[10px] text-tealslate uppercase tracking-widest mb-1 font-bold" data-id="element-2007">
              Today's Workout
            </p>
            <h3 className="text-xl font-serif text-white leading-tight mb-1" data-id="element-2008">
              Upper Body
            </h3>
            <p className="text-[10px] text-[#9CA3AF] mb-5" data-id="element-2009">
              Week 3 · Day 2 · 45 min
            </p>

            {/* Progress bar */}
            <div className="bg-darkgray h-1 rounded-full overflow-hidden mb-6" data-id="element-2010">
              <div className="bg-tealslate h-full w-[35%] rounded-full" data-id="element-2011" />
            </div>

            {/* Exercise list */}
            <div className="space-y-2" data-id="element-2012">
              {[{
              name: 'Incline DB Press',
              sets: '4 × 8',
              done: true
            }, {
              name: 'Cable Row',
              sets: '4 × 10',
              done: true
            }, {
              name: 'Lat Pulldown',
              sets: '3 × 12',
              done: false,
              active: true
            }, {
              name: 'Lateral Raise',
              sets: '3 × 15',
              done: false
            }, {
              name: 'Tricep Pushdown',
              sets: '3 × 12',
              done: false
            }].map((ex, i) => <div key={i} className={`flex items-center gap-3 p-2.5 rounded ${ex.active ? 'bg-tealslate/20 border-l-2 border-tealslate' : ex.done ? 'bg-navyslate/30 opacity-60' : 'bg-navyslate/30'}`} data-id="element-2013">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${ex.done ? 'bg-tealslate' : 'border border-[#9CA3AF]'}`} data-id="element-2014">
                    {ex.done && <Check className="w-3 h-3 text-white" data-id="element-2015" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2016">
                    <p className="text-[10px] font-bold text-white truncate" data-id="element-2017">
                      {ex.name}
                    </p>
                    <p className="text-[8px] text-[#9CA3AF]" data-id="element-2018">{ex.sets}</p>
                  </div>
                  {ex.active && <PlayCircle className="w-4 h-4 text-tealslate shrink-0" data-id="element-2019" />}
                </div>)}
            </div>

            {/* CTA at bottom */}
            <div className="mt-5 bg-tealslate text-center py-3 rounded" data-id="element-2020">
              <p className="text-[10px] font-bold text-white uppercase tracking-widest" data-id="element-2021">
                Continue Workout
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Glow effect */}
      <div className="absolute inset-0 bg-tealslate/10 blur-3xl -z-10" data-id="element-2022" />
    </div>;
}
export function Training() {
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-2023">
      {/* HERO */}
      <section className="bg-bodybg pt-32 pb-20 md:pb-28 px-6 border-b border-darkgray relative overflow-hidden" data-id="element-2024">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-tealslate/5 rounded-full blur-3xl" data-id="element-2025" />

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-2026">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2027">
            {/* Left: Pitch */}
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} data-id="element-2028">
              <motion.p variants={fadeUpVariants} className="text-[10px] md:text-xs font-bold text-tealslate uppercase tracking-[0.25em] mb-6" data-id="element-2029">
                Structured Training App
              </motion.p>
              <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif text-white leading-[1.05] mb-6" data-id="element-2030">
                Structured training. Built for the way you live now.
              </motion.h1>
              <motion.div variants={fadeUpVariants} className="w-12 h-0.5 bg-tealslate mb-6" data-id="element-2031" />
              <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#D8D8D0] leading-relaxed mb-10 max-w-xl" data-id="element-2032">
                Programmed workouts designed to preserve muscle, build strength,
                and maximize the results of your GLP-1 journey — delivered
                through a coach-built training app.
              </motion.p>

              {/* Price */}
              <motion.div variants={fadeUpVariants} className="flex items-baseline gap-3 mb-8" data-id="element-2033">
                <span className="text-5xl md:text-6xl font-serif text-white" data-id="element-2034">
                  $6.99
                </span>
                <span className="text-sm text-[#9CA3AF] uppercase tracking-widest" data-id="element-2035">
                  / month
                </span>
              </motion.div>

              {/* CTAs */}
              <motion.div variants={fadeUpVariants} className="flex flex-col sm:flex-row gap-4" data-id="element-2036">
                <a href={TRAINERIZE_URL} target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors text-center inline-flex items-center justify-center gap-2" data-id="element-2037">
                  Start Your Program <ArrowRight className="w-4 h-4" data-id="element-2038" />
                </a>
                <a href="#how-it-works" className="px-8 py-4 border border-[#D8D8D0] text-[#D8D8D0] text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-colors text-center" data-id="element-2039">
                  How It Works
                </a>
              </motion.div>

              <motion.p variants={fadeUpVariants} className="text-xs text-[#7A7A70] italic mt-6" data-id="element-2040">
                Cancel anytime · No long-term commitment
              </motion.p>
            </motion.div>

            {/* Right: Phone mockup */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            y: 30
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            duration: 1,
            delay: 0.3,
            ease: [0.2, 0.65, 0.3, 0.9]
          }} className="flex justify-center" data-id="element-2041">
              <PhoneMockup data-id="element-2042" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="bg-[#F5F5F0] py-20 md:py-28 px-6" data-id="element-2043">
        <div className="max-w-7xl mx-auto" data-id="element-2044">
          <div className="text-center mb-16" data-id="element-2045">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-2046">
              What's Included
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] max-w-3xl mx-auto leading-tight" data-id="element-2047">
              Everything you need to train with intention.
            </h2>
            <div className="w-12 h-0.5 bg-tealslate mx-auto mt-6" data-id="element-2048" />
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2049">
            {[{
            icon: Dumbbell,
            title: 'Programmed Workouts',
            desc: 'Progressive routines designed specifically for GLP-1 users — engineered to preserve lean mass while you lose fat.'
          }, {
            icon: PlayCircle,
            title: 'Video Demos',
            desc: 'Every exercise comes with form demonstrations so you train safely and effectively, even as a beginner.'
          }, {
            icon: TrendingUp,
            title: 'Progress Tracking',
            desc: 'Log sets, reps, and weights. Watch your strength climb even as the scale moves down.'
          }, {
            icon: MessageCircle,
            title: 'Direct Messaging',
            desc: 'Send me a question, photo, or check-in anytime. I review and respond personally.'
          }, {
            icon: Calendar,
            title: 'Habit Tracking',
            desc: 'Track protein intake, hydration, sleep, and steps alongside your workouts.'
          }, {
            icon: Smartphone,
            title: 'Mobile-First',
            desc: 'Train anywhere — at the gym, at home, or traveling. Your program goes with you.'
          }].map((feature, i) => <motion.div key={i} variants={fadeUpVariants} className="bg-white border border-[#D8D8D0] p-8 flex flex-col" data-id="element-2050">
                <feature.icon className="w-7 h-7 text-tealslate mb-5" data-id="element-2051" />
                <h3 className="text-xl font-serif text-[#1F1F1F] mb-3" data-id="element-2052">
                  {feature.title}
                </h3>
                <p className="text-sm text-[#7A7A70] leading-relaxed" data-id="element-2053">
                  {feature.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="bg-bodybg py-20 md:py-28 px-6 border-t border-darkgray" data-id="element-2054">
        <div className="max-w-7xl mx-auto" data-id="element-2055">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2056">
            <motion.div variants={fadeUpVariants} className="lg:col-span-1" data-id="element-2057">
              <p className="text-[10px] font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-4" data-id="element-2058">
                Who It's For
              </p>
              <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-6" data-id="element-2059">
                Built for the GLP-1 era of training.
              </h2>
              <div className="w-12 h-0.5 bg-tealslate" data-id="element-2060" />
            </motion.div>

            <motion.div variants={fadeUpVariants} className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2061">
              {[{
              label: 'GLP-1 Users',
              desc: 'Anyone on Ozempic, Wegovy, Zepbound, or similar — protect muscle while losing fat.'
            }, {
              label: 'Post-Medication',
              desc: 'Maintain your results and lock in the habits after stepping off the medication.'
            }, {
              label: 'Returning Lifters',
              desc: 'Ramp back into structured training with a plan, not a guess.'
            }, {
              label: 'Total Beginners',
              desc: 'Never lifted before? The program meets you where you are with video demos for everything.'
            }].map((item, i) => <div key={i} className="border-l-2 border-tealslate pl-5 py-2" data-id="element-2062">
                  <h3 className="text-lg font-serif text-white mb-2" data-id="element-2063">
                    {item.label}
                  </h3>
                  <p className="text-sm text-[#9CA3AF] leading-relaxed" data-id="element-2064">
                    {item.desc}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="bg-[#F5F5F0] py-20 md:py-28 px-6" data-id="element-2065">
        <div className="max-w-5xl mx-auto" data-id="element-2066">
          <div className="text-center mb-16" data-id="element-2067">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-2068">
              How It Works
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F]" data-id="element-2069">
              Three steps to start training.
            </h2>
            <div className="w-12 h-0.5 bg-tealslate mx-auto mt-6" data-id="element-2070" />
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="space-y-12" data-id="element-2071">
            {[{
            step: '01',
            title: 'Sign up online',
            desc: "Click the link below and create your Structured account. You'll set up your profile and payment in under 2 minutes."
          }, {
            step: '02',
            title: 'Download the ABC Trainerize app',
            desc: 'Structured runs on the ABC Trainerize platform. Download the app from the App Store or Google Play, and log in with your new account.'
          }, {
            step: '03',
            title: 'Start your first workout',
            desc: "Your program will be waiting. Open today's session, watch the demos, log your sets, and start building."
          }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start pb-12 border-b border-[#D8D8D0] last:border-0" data-id="element-2072">
                <div className="md:col-span-2" data-id="element-2073">
                  <span className="text-6xl font-serif text-tealslate" data-id="element-2074">
                    {item.step}
                  </span>
                </div>
                <div className="md:col-span-10" data-id="element-2075">
                  <h3 className="text-2xl md:text-3xl font-serif text-[#1F1F1F] mb-3" data-id="element-2076">
                    {item.title}
                  </h3>
                  <p className="text-base text-[#3B3B3B] leading-relaxed max-w-2xl" data-id="element-2077">
                    {item.desc}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>

          {/* Store badges */}
          <div className="mt-16 text-center" data-id="element-2078">
            <p className="text-[10px] font-bold text-[#7A7A70] uppercase tracking-[0.25em] mb-6" data-id="element-2079">
              Where to download the app
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-2080">
              <a href="https://apps.apple.com/us/app/abc-trainerize/id567761305" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-charcoal text-white px-6 py-3 hover:bg-darkgray transition-colors min-w-[180px]" data-id="element-2081">
                <Apple className="w-7 h-7" data-id="element-2082" />
                <div className="text-left" data-id="element-2083">
                  <p className="text-[9px] uppercase tracking-wider opacity-70" data-id="element-2084">
                    Download on the
                  </p>
                  <p className="text-base font-bold leading-tight" data-id="element-2085">App Store</p>
                </div>
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.trainerize" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-charcoal text-white px-6 py-3 hover:bg-darkgray transition-colors min-w-[180px]" data-id="element-2086">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor" data-id="element-2087">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.119 12l2.579-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303L5.864 2.658z" data-id="element-2088" />
                </svg>
                <div className="text-left" data-id="element-2089">
                  <p className="text-[9px] uppercase tracking-wider opacity-70" data-id="element-2090">
                    Get it on
                  </p>
                  <p className="text-base font-bold leading-tight" data-id="element-2091">
                    Google Play
                  </p>
                </div>
              </a>
            </div>
            <p className="text-xs text-[#7A7A70] italic mt-6 max-w-md mx-auto" data-id="element-2092">
              The app is called <strong data-id="element-2093">ABC Trainerize</strong> — your
              Structured program lives inside it once you sign up.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-bodybg py-20 md:py-28 px-6 border-t border-darkgray" data-id="element-2094">
        <div className="max-w-4xl mx-auto" data-id="element-2095">
          <div className="text-center mb-16" data-id="element-2096">
            <p className="text-[10px] font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-4" data-id="element-2097">
              Common Questions
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-white" data-id="element-2098">
              Frequently asked.
            </h2>
            <div className="w-12 h-0.5 bg-tealslate mx-auto mt-6" data-id="element-2099" />
          </div>

          <div className="space-y-6" data-id="element-2100">
            {[{
            q: 'Do I need a gym membership?',
            a: "Some programs are gym-based, others can be done at home with minimal equipment. Let me know your setup when you sign up and I'll match you to the right track."
          }, {
            q: "I've never lifted weights before. Is this for me?",
            a: 'Absolutely. Every exercise has video demonstrations, and the programs start at a true beginner level. The point is to build a foundation, not crush you on day one.'
          }, {
            q: 'Why is the app called "ABC Trainerize" instead of "Structured"?',
            a: 'ABC Trainerize is the underlying coaching platform — Structured is my program inside it. The experience inside the app is fully branded and built around your goals.'
          }, {
            q: 'Can I cancel anytime?',
            a: 'Yes — there are no long-term commitments. Pause or cancel from your account whenever you need to.'
          }, {
            q: 'Do I get nutrition coaching too?',
            a: 'The app focuses on training and habit tracking. For 1:1 nutrition coaching, check out the Services page or get in touch directly.'
          }].map((item, i) => <details key={i} className="group bg-navyslate/30 border border-darkgray p-6 hover:border-tealslate/50 transition-colors" data-id="element-2101">
                <summary className="cursor-pointer flex items-center justify-between gap-4 list-none" data-id="element-2102">
                  <h3 className="text-lg font-serif text-white" data-id="element-2103">{item.q}</h3>
                  <span className="text-tealslate text-2xl group-open:rotate-45 transition-transform shrink-0" data-id="element-2104">
                    +
                  </span>
                </summary>
                <p className="text-sm text-[#D8D8D0] leading-relaxed mt-4 pt-4 border-t border-darkgray" data-id="element-2105">
                  {item.a}
                </p>
              </details>)}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-burgundy py-20 md:py-28 px-6 text-center" data-id="element-2106">
        <div className="max-w-3xl mx-auto" data-id="element-2107">
          <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-2108">
            Get Started
          </p>
          <h2 className="text-4xl md:text-6xl font-serif text-white leading-tight mb-8" data-id="element-2109">
            Build your structure. Starting today.
          </h2>
          <p className="text-lg text-[#D8D8D0] leading-relaxed mb-10 max-w-xl mx-auto" data-id="element-2110">
            Join Structured for less than the cost of one coffee a week. No
            long-term commitment, cancel anytime.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6" data-id="element-2111">
            <a href={TRAINERIZE_URL} target="_blank" rel="noopener noreferrer" className="px-10 py-5 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-lightteal transition-colors inline-flex items-center justify-center gap-3" data-id="element-2112">
              Start Your Program — $6.99/mo <ArrowRight className="w-4 h-4" data-id="element-2113" />
            </a>
            <Link to="/contact" className="text-sm font-bold text-[#D8D8D0] uppercase tracking-wider hover:text-white transition-colors" data-id="element-2114">
              Have questions? →
            </Link>
          </div>

          <p className="text-xs text-[#9CA3AF] italic" data-id="element-2115">
            30-day satisfaction guarantee · Cancel anytime
          </p>
        </div>
      </section>
    </div>;
}